import React, { useEffect, useRef, useState } from "react";

const currentState = [
  "Different understanding",
  "Different priorities",
  "Different decisions",
  "Duplicated / wasted effort",
  "Slower execution",
  "Missed opportunities",
  "Lost growth",
];

const neededState = [
  "One context",
  "One strategy",
  "Shared priorities",
  "Aligned decisions",
  "Coordinated execution",
  "Faster learning",
  "Better growth",
];

const AlignmentStatement: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(motionQuery.matches);
    if (motionQuery.matches) {
      return;
    }

    let frame = 0;
    const updateProgress = () => {
      frame = 0;
      const rect = section.getBoundingClientRect();
      const scrollableDistance = Math.max(section.offsetHeight - window.innerHeight, 1);
      const nextProgress = Math.min(Math.max(-rect.top / scrollableDistance, 0), 1);
      setProgress(nextProgress);
    };

    const handleScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(updateProgress);
    };

    updateProgress();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  const firstStageVisible = reducedMotion || progress < 0.28;
  const quoteVisible = !reducedMotion && progress >= 0.28 && progress < 0.58;
  const comparisonVisible = reducedMotion || progress >= 0.58;
  const firstPhraseFilled = reducedMotion || progress >= 0.08;
  const secondPhraseFilled = reducedMotion || progress >= 0.18;

  return (
    <section
      ref={sectionRef}
      className="relative h-[300vh] bg-white section-padding motion-reduce:h-auto motion-reduce:py-20"
      aria-label="From fragmented work to aligned growth"
    >
      <div className="sticky top-0 flex min-h-screen items-center overflow-hidden py-20 motion-reduce:static motion-reduce:block motion-reduce:min-h-0">
        <div className="container relative mx-auto min-h-[620px] max-w-6xl md:min-h-[680px]">
          <div
            className={`absolute inset-0 flex items-center justify-center text-center transition-all duration-700 motion-reduce:relative motion-reduce:mb-20 motion-reduce:opacity-100 ${
              firstStageVisible ? "translate-y-0 opacity-100" : "-translate-y-8 opacity-0 pointer-events-none"
            }`}
            aria-hidden={!firstStageVisible}
          >
            <h2 className="font-display text-6xl font-bold leading-[0.95] md:text-8xl lg:text-9xl">
              <span className={`block transition-colors duration-700 ${firstPhraseFilled ? "text-deep-blue" : "text-deep-blue/15"}`}>
                Context in.
              </span>
              <span className={`block transition-colors duration-700 ${secondPhraseFilled ? "text-deep-blue" : "text-deep-blue/15"}`}>
                Impact out.
              </span>
            </h2>
          </div>

          <div
            className={`absolute inset-0 flex items-center justify-center transition-all duration-700 motion-reduce:relative motion-reduce:mb-20 motion-reduce:opacity-100 ${
              quoteVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0 pointer-events-none"
            }`}
            aria-hidden={!quoteVisible && !reducedMotion}
          >
            <blockquote className="max-w-5xl text-center font-display text-4xl font-bold leading-tight text-deep-blue md:text-6xl lg:text-7xl">
              “Your strategy is only as valuable as your team&apos;s ability to execute it together.”
            </blockquote>
          </div>

          <div
            className={`absolute inset-0 flex items-center transition-all duration-700 motion-reduce:relative motion-reduce:opacity-100 ${
              comparisonVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0 pointer-events-none"
            }`}
            aria-hidden={!comparisonVisible}
          >
            <div className="grid w-full grid-cols-2 gap-5 md:gap-16">
              {[{ heading: "What you have", items: currentState }, { heading: "What you need", items: neededState }].map((column, columnIndex) => (
                <div key={column.heading} className={columnIndex === 1 ? "border-l border-teal-green/40 pl-5 md:pl-16" : ""}>
                  <h2 className={`mb-6 font-display text-2xl font-bold md:mb-8 md:text-4xl ${columnIndex === 1 ? "text-teal-green" : "text-deep-blue"}`}>
                    {column.heading}
                  </h2>
                  <ol className="space-y-3 md:space-y-4">
                    {column.items.map((item, index) => (
                      <li
                        key={item}
                        className={`flex items-start gap-2 text-sm font-bold leading-snug text-deep-blue transition-all duration-500 md:gap-3 md:text-xl ${
                          comparisonVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                        }`}
                        style={{ transitionDelay: reducedMotion ? "0ms" : `${columnIndex * 240 + index * 90}ms` }}
                      >
                        <span className={`mt-0.5 font-display text-xs md:text-sm ${columnIndex === 1 ? "text-teal-green" : "text-deep-blue/40"}`} aria-hidden="true">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AlignmentStatement;
